/**
 * Deal columns Postgres will not accept an empty string in.
 *
 * A text column takes '' happily. A numeric or date column answers the
 * whole insert with `400 invalid input syntax for type numeric: ""` —
 * and because the insert is one statement, one blank field fails the
 * entire deal.
 *
 * The app writes '' for "not filled in" everywhere: an untouched input,
 * a `contact.nma || ''` default. So the coercion belongs at the single
 * place that knows which column is which, rather than in each of the
 * dozens of callers that build a deal. Convert to Deal sent
 * `acreage: ''` for a lead with no NMA recorded — the normal case — and
 * the conversion died on it.
 */

/**
 * Every numeric, date and timestamptz column on `deals`, as reported by
 * information_schema. A FREE-TEXT column must NOT be listed: '' is a
 * legitimate value there, and nulling it would silently discard what
 * somebody typed and then cleared.
 *
 * A text column carrying a FOREIGN KEY is the exception, and is listed.
 * '' is not a value there — no row has id '' — so it cannot be
 * something somebody meant to keep; it is what a `<select>` with a
 * "— None —" option hands back, and it fails the key rather than
 * clearing it. NULL is how "not set" is spelled in those columns.
 */
export const DEAL_EMPTY_TO_NULL = new Set([
  // numeric
  'acreage',
  'asking_price',
  'buy_rate_nra',
  'coordinator_commission_pct',
  'nma',
  'offer_amount',
  'price_per_nma',
  'sell_price',
  'sell_rate_nra',
  // date
  'buy_closing_date',
  'closing_date',
  'effective_date',
  'sell_closing_date',
  // timestamptz
  'asset_listed_at',
  'passed_at',
  // uuid — every call site currently guards these with `|| null`, but
  // they are the same failure one step away: an agent dropdown with an
  // "unassigned" option hands back '', and uuid refuses it exactly as
  // numeric does. Closing three types and leaving the fourth open is
  // not a fix, it is a slower version of the same bug.
  'buy_side_agent_id',
  'coordinator_id',
  'passed_by',
  'sell_side_agent_id',
  // numeric / date, added with investor placement
  'investor_capital',
  'investor_placed_on',
  // text, but each is a foreign key — see the note above. The investor
  // and entity dropdowns both carry a "— None —" option, and unplacing
  // a deal means clearing them.
  'investor_id',
  'investor_entity_id',
]);

/**
 * '' (or whitespace) becomes NULL for the columns that demand it, and
 * is left alone everywhere else.
 *
 * Deliberately not a number parse: a bad numeric string should still
 * reach Postgres and fail loudly rather than be silently nulled, which
 * would drop a price somebody actually entered.
 */
export function blankToNull(column, value) {
  if (!DEAL_EMPTY_TO_NULL.has(column)) return value;
  if (value === null || value === undefined) return null;
  return typeof value === 'string' && value.trim() === '' ? null : value;
}
